<div
  *ngIf="domOpen"
  #overlayRoot
  class="v2-album-overlay"
  [class.v2-album-overlay--closing]="isClosing"
  tabindex="-1"
  role="dialog"
  aria-modal="true"
  [attr.aria-label]="'ALBUM_VIEW' | translate"
>

  <!-- ——— First page (top-left) ——— -->
  <button
    type="button"
    class="v2-ao-first-page"
    (click)="goToPage(0)"
    [class.v2-ao-first-page--hidden]="totalPages < 2"
    [disabled]="activePageIndex <= 0 || totalPages < 2"
    [attr.aria-disabled]="activePageIndex <= 0 ? true : null"
    [attr.aria-label]="'ALBUM_FIRST_PAGE' | translate"
  >
    <ion-icon name="play-skip-back-outline" aria-hidden="true"></ion-icon>
  </button>

  <!-- ——— Floating close (top-right, same as v3-dc-floating-close) ——— -->
  <button
    type="button"
    class="v2-ao-close"
    (click)="close()"
    [attr.aria-label]="'BUTTON_CLOSE' | translate"
  >×</button>

  <!-- Assertive live region: announces page changes to screen readers on every commit -->
  <span class="v2-ao-sr-only" aria-live="assertive" aria-atomic="true">{{ pageAnnouncement }}</span>

  <!-- ——— "Your Album" label pill (top-center, same style as v3-dc-binders-remaining) ——— -->
  <div class="v2-ao-title-pill" aria-hidden="true">
    {{ 'ALBUM_VIEW' | translate }}
  </div>

  <div
    *ngIf="showAlbumLayoutLoading"
    class="v2-ao-layout-loading"
    role="status"
    aria-live="polite"
    [attr.aria-label]="'LOADING' | translate"
  >
    <div #albumLayoutLoader class="loader v2-ao-layout-loading__lottie" aria-hidden="true"></div>
    <p class="v2-ao-layout-loading__label">{{ 'LOADING' | translate }}</p>
  </div>

  <!-- ——— Scrollable album page area ——— -->
  <div class="v2-ao-body" [class.v2-ao-body--layout-pending]="showAlbumLayoutLoading">
    <div
      #scrollCenter
      class="v2-ao-scroll-center"
      [class.v2-ao-scroll-center--zoomed]="albumAreaZoomed"
    >
      <div
        #zoomSurface
        class="v2-ao-zoom-surface"
        [style.transform]="getAlbumZoomTransform()"
      >
      <div class="album-card-block" #albumCardBlock>
        <div class="v2-ao-carousel-shell">

        <!-- Carousel viewport: gesture target; overflow visible so scaled neighbors peek -->
        <div #carouselViewport class="v2-ao-carousel-viewport">

          <!-- Carousel track: translated horizontally to bring active slide into view -->
          <div #carouselTrack class="v2-ao-carousel-track">

            <ng-container *ngFor="let pageIdx of uiAlbumPageIndices; trackBy: trackUiPageByIndex">
              <div class="v2-ao-carousel-slide">
                <!-- Slide inner: receives scale + opacity via Angular binding in static state;
                     overridden directly during drag for 1:1 tracking -->
                <div
                  #carouselSlideInner
                  class="v2-ao-carousel-slide-inner"
                  [class.v2-ao-carousel-slide-inner--inactive]="pageIdx !== activePageIndex"
                  [style.transform]="getSlideTransform(pageIdx)"
                  [style.opacity]="getSlideOpacity(pageIdx)"
                  [style.z-index]="getSlideZIndex(pageIdx)"
                  [attr.aria-hidden]="pageIdx !== activePageIndex ? true : null"
                >
                  <div class="album-page">
                    <div
                      *ngIf="slideShowsAlbumSkeleton(pageIdx)"
                      class="v2-ao-slide-skeleton"
                      [class.v2-ao-slide-skeleton--blocking]="pageIdx === activePageIndex"
                      [attr.aria-busy]="pageIdx === activePageIndex ? true : null"
                      [attr.aria-hidden]="pageIdx !== activePageIndex ? true : null"
                    >
                      <!-- INSERT_PAGE is a full-bleed image only — minimal placeholder -->
                      <div
                        *ngIf="pageName(pageIdx) === 'INSERT_PAGE'"
                        class="v2-ao-album-skeleton-card v2-ao-album-skeleton-card--insert"
                      >
                        <div
                          class="v2-ao-album-skeleton-insert v2-ao-album-skeleton-shimmer"
                          aria-hidden="true"
                        ></div>
                        <p *ngIf="pageIdx === activePageIndex" class="v2-ao-album-skeleton-caption">
                          {{ 'LOADING' | translate }}
                        </p>
                      </div>
                      <div
                        *ngIf="pageName(pageIdx) !== 'INSERT_PAGE'"
                        class="v2-ao-album-skeleton-card"
                      >
                        <div class="v2-ao-album-skeleton-top" aria-hidden="true">
                          <span class="v2-ao-album-skeleton-logo v2-ao-album-skeleton-shimmer"></span>
                          <div
                            class="v2-ao-album-skeleton-header v2-ao-album-skeleton-shimmer"
                          ></div>
                          <span class="v2-ao-album-skeleton-logo v2-ao-album-skeleton-shimmer"></span>
                        </div>
                        <div class="v2-ao-album-skeleton-grid" aria-hidden="true">
                          <span class="v2-ao-album-skeleton-cell v2-ao-album-skeleton-shimmer"></span>
                          <span class="v2-ao-album-skeleton-cell v2-ao-album-skeleton-shimmer"></span>
                          <span class="v2-ao-album-skeleton-cell v2-ao-album-skeleton-shimmer"></span>
                          <span class="v2-ao-album-skeleton-cell v2-ao-album-skeleton-shimmer"></span>
                          <span class="v2-ao-album-skeleton-cell v2-ao-album-skeleton-shimmer"></span>
                          <span class="v2-ao-album-skeleton-cell v2-ao-album-skeleton-shimmer"></span>
                        </div>
                        <div
                          class="v2-ao-album-skeleton-footer v2-ao-album-skeleton-shimmer"
                          aria-hidden="true"
                        ></div>
                        <p *ngIf="pageIdx === activePageIndex" class="v2-ao-album-skeleton-caption">
                          {{ 'LOADING' | translate }}
                        </p>
                      </div>
                    </div>

                    <!-- INSERT_PAGE / CARD_PAGE -->
                    <ng-container *ngIf="pageName(pageIdx) === 'INSERT_PAGE' || pageName(pageIdx) === 'CARD_PAGE'">
                      <img
                        class="album-background v2-ao-album-img"
                        [src]="albumBackgroundImageSrc(getPageDef(pageIdx).background)"
                        loading="eager"
                        decoding="async"
                        alt=""
                      />

                      <section
                        [style.height]="'100%'"
                        [style.width]="'95%'"
                        [style.position]="'absolute'"
                        [style.top]="'5px'"
                        [style.margin-left]="'5px'"
                        [style.margin-right]="'5px'"
                      >
                        <ion-row>
                          <ion-col size="2">
                            <img
                              class="card-page-left-logo v2-ao-album-img"
                              *ngIf="getPageDef(pageIdx).leftLogo"
                              [src]="getPageDef(pageIdx).leftLogo"
                              loading="eager"
                              decoding="async"
                              alt=""
                            />
                          </ion-col>
                          <ion-col size="8">
                            <div *ngIf="pageName(pageIdx) !== 'INSERT_PAGE'"
                              class="card-page-header"
                              [style.color]="getPageDef(pageIdx).sectionHeaderColor"
                              [style.font-size]="'20px'"
                              [style.height]="'100%'"
                              [style.padding-top]="'3%'"
                            >{{ getPageDef(pageIdx).sectionHeader }}</div>
                          </ion-col>
                          <ion-col size="2">
                            <img
                              class="card-page-right-logo v2-ao-album-img"
                              *ngIf="getPageDef(pageIdx).rightLogo"
                              [src]="getPageDef(pageIdx).rightLogo"
                              loading="eager"
                              decoding="async"
                              alt=""
                            />
                          </ion-col>
                        </ion-row>

                        <ion-row
                          *ngFor="let row of (getPageDef(pageIdx).newCardRows || []); let rowIndex = index"
                          [style.height]="'21%'"
                          [style.margin-right]="'2%'"
                          [style.margin-left]="'2%'"
                        >
                          <ion-col
                            *ngFor="let ori of row.orientation; let i = index"
                            [size]="row.colSize[i]"
                            class="card-col"
                          >
                            <ng-container
                              *ngIf="row.item[i] !== null && row.item[i] !== 'empty_spot' && row.item[i] !== 'custom_image' && ori === 'l2'"
                            >
                              <ng-container *ngIf="getAlbumStickerSlot(row.item[i].item_id, 'L') as stickerSlot">
                                <div
                                  class="l2div v2-ao-album-sticker-slot"
                                  [class.v2-ao-album-sticker-slot--locked]="!stickerSlot.owned"
                                  role="button"
                                  tabindex="0"
                                  (click)="onAlbumStickerClick(row.item[i].item_id, $event)"
                                  (keydown.enter)="onAlbumStickerClick(row.item[i].item_id, $event)"
                                >
                                  <img
                                    *ngIf="stickerSlot.src"
                                    class="card4l2 v2-ao-album-img"
                                    [src]="stickerSlot.src"
                                    loading="eager"
                                    decoding="async"
                                    alt=""
                                  />
                                  <div
                                    *ngIf="!stickerSlot.src"
                                    class="v2-ao-album-sticker-placeholder"
                                    aria-hidden="true"
                                  ></div>
                                  <div
                                    *ngIf="!stickerSlot.owned"
                                    class="v2-ao-album-locked-overlay"
                                    aria-hidden="true"
                                  >
                                    <div class="v2-ao-album-locked-icon-wrap">
                                      <svg
                                        class="v2-ao-album-locked-icon"
                                        viewBox="0 0 24 24"
                                        fill="none"
                                        stroke="currentColor"
                                        stroke-width="2"
                                        stroke-linecap="round"
                                        stroke-linejoin="round"
                                        xmlns="http://www.w3.org/2000/svg"
                                      >
                                        <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                                        <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                                      </svg>
                                    </div>
                                  </div>
                                </div>
                              </ng-container>
                            </ng-container>

                            <ng-container
                              *ngIf="row.item[i] !== null && row.item[i] !== 'empty_spot' && row.item[i] !== 'custom_image' && ori === 'p'"
                            >
                              <ng-container *ngIf="getAlbumStickerSlot(row.item[i].item_id, 'P') as stickerSlot">
                                <div
                                  class="pdiv v2-ao-album-sticker-slot"
                                  [class.v2-ao-album-sticker-slot--locked]="!stickerSlot.owned"
                                  role="button"
                                  tabindex="0"
                                  (click)="onAlbumStickerClick(row.item[i].item_id, $event)"
                                  (keydown.enter)="onAlbumStickerClick(row.item[i].item_id, $event)"
                                >
                                  <img
                                    *ngIf="stickerSlot.src"
                                    class="card4p v2-ao-album-img"
                                    [src]="stickerSlot.src"
                                    loading="eager"
                                    decoding="async"
                                    alt=""
                                  />
                                  <div
                                    *ngIf="!stickerSlot.src"
                                    class="v2-ao-album-sticker-placeholder"
                                    aria-hidden="true"
                                  ></div>
                                  <div
                                    *ngIf="!stickerSlot.owned"
                                    class="v2-ao-album-locked-overlay"
                                    aria-hidden="true"
                                  >
                                    <div class="v2-ao-album-locked-icon-wrap">
                                      <svg
                                        class="v2-ao-album-locked-icon"
                                        viewBox="0 0 24 24"
                                        fill="none"
                                        stroke="currentColor"
                                        stroke-width="2"
                                        stroke-linecap="round"
                                        stroke-linejoin="round"
                                        xmlns="http://www.w3.org/2000/svg"
                                      >
                                        <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                                        <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                                      </svg>
                                    </div>
                                  </div>
                                </div>
                              </ng-container>
                            </ng-container>

                            <div
                              *ngIf="row.item[i] === 'custom_image' && ori === 'p'"
                              class="pdiv transparent"
                            >
                              <img class="custom-image v2-ao-album-img" [src]="row.custom_image[i]" loading="eager" decoding="async" alt="" />
                            </div>

                            <ng-container
                              *ngIf="row.item[i] !== null && row.item[i] !== 'empty_spot' && row.item[i] !== 'custom_image' && ori === 'l'"
                            >
                              <ng-container *ngIf="getAlbumStickerSlot(row.item[i].item_id, 'L') as stickerSlot">
                                <div
                                  class="ldiv v2-ao-album-sticker-slot"
                                  [class.v2-ao-album-sticker-slot--locked]="!stickerSlot.owned"
                                  role="button"
                                  tabindex="0"
                                  (click)="onAlbumStickerClick(row.item[i].item_id, $event)"
                                  (keydown.enter)="onAlbumStickerClick(row.item[i].item_id, $event)"
                                >
                                  <img
                                    *ngIf="stickerSlot.src"
                                    class="card4l v2-ao-album-img"
                                    [src]="stickerSlot.src"
                                    loading="eager"
                                    decoding="async"
                                    alt=""
                                  />
                                  <div
                                    *ngIf="!stickerSlot.src"
                                    class="v2-ao-album-sticker-placeholder"
                                    aria-hidden="true"
                                  ></div>
                                  <div
                                    *ngIf="!stickerSlot.owned"
                                    class="v2-ao-album-locked-overlay"
                                    aria-hidden="true"
                                  >
                                    <div class="v2-ao-album-locked-icon-wrap">
                                      <svg
                                        class="v2-ao-album-locked-icon"
                                        viewBox="0 0 24 24"
                                        fill="none"
                                        stroke="currentColor"
                                        stroke-width="2"
                                        stroke-linecap="round"
                                        stroke-linejoin="round"
                                        xmlns="http://www.w3.org/2000/svg"
                                      >
                                        <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                                        <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                                      </svg>
                                    </div>
                                  </div>
                                </div>
                              </ng-container>
                            </ng-container>

                            <div
                              *ngIf="row.item[i] === 'custom_image' && ori === 'l'"
                              class="ldiv transparent"
                            >
                              <img class="custom-image v2-ao-album-img" [src]="row.custom_image[i]" loading="eager" decoding="async" alt="" />
                            </div>
                          </ion-col>
                        </ion-row>
                      </section>

                      <ion-row [style.width]="'100%'" [style.position]="'absolute'" [style.bottom]="'1%'">
                        <ion-col
                          *ngIf="getPageDef(pageIdx).page?.name !== 'INSERT_PAGE'"
                          class="card-page-footer"
                          [style.color]="'white'"
                          [style.font-size]="'15px'"
                        >{{ getPageDef(pageIdx).sectionFooter }}</ion-col>
                      </ion-row>
                    </ng-container>

                    <!-- DRAGGABLE_PAGE -->
                    <ng-container *ngIf="pageName(pageIdx) === 'DRAGGABLE_PAGE'">
                      <div
                        class="scalable-container"
                        [ngStyle]="{
                          'width.px': getDraggableLayout(pageIdx)?.width ?? null,
                          'height.px': getDraggableLayout(pageIdx)?.height ?? null
                        }"
                      >
                        <img
                          class="album-background v2-ao-album-img"
                          [style.transform]="'scale(1.04)'"
                          [src]="draggableBackgroundSrc(getPageDef(pageIdx))"
                          loading="eager"
                          decoding="async"
                          alt=""
                        />

                        <section class="draggable-section">
                          <div *ngFor="let card of (getPageDef(pageIdx).cards || []); let cardIndex = index">
                            <div
                              style="position: absolute"
                              [ngStyle]="{
                                'margin-left': getDraggableLayout(pageIdx)?.draggableCards[cardIndex]?.scaledMarginLeft,
                                'margin-top': getDraggableLayout(pageIdx)?.draggableCards[cardIndex]?.scaledMarginTop
                              }"
                              [style.transform]="'scale(' + (card?.scale ?? 1) + ') rotate(' + (card?.rotation ?? 0) + 'deg)'"
                            >
                              <ng-container
                                *ngIf="getAlbumStickerSlot(card.item_id, card.orientation === 'P' ? 'P' : 'L') as stickerSlot"
                              >
                                <!-- Direct-upload final image: native aspect, not forced P/L frame -->
                                <div
                                  *ngIf="isDraggableInsertedSticker(card)"
                                  class="v2-ao-album-sticker-slot v2-ao-draggable-inserted-wrap"
                                  [class.v2-ao-album-sticker-slot--locked]="!stickerSlot.owned"
                                  role="button"
                                  tabindex="0"
                                  (click)="onAlbumStickerClick(card.item_id, $event)"
                                  (keydown.enter)="onAlbumStickerClick(card.item_id, $event)"
                                >
                                  <img
                                    *ngIf="stickerSlot.src"
                                    class="v2-ao-album-img v2-ao-draggable-inserted-img"
                                    [src]="stickerSlot.src"
                                    (load)="onInsertedDraggableImageLoad(pageIdx, cardIndex, card.item_id, $event)"
                                    [style.width.px]="getDraggableLayout(pageIdx)?.draggableCards[cardIndex]?.insertedRenderW"
                                    [style.height.px]="getDraggableLayout(pageIdx)?.draggableCards[cardIndex]?.insertedRenderH"
                                    [style.max-width.px]="
                                      !getDraggableLayout(pageIdx)?.draggableCards[cardIndex]?.insertedRenderW
                                        ? getDraggableLayout(pageIdx)?.draggableCards[cardIndex]?.insertedPendingMaxW
                                        : null
                                    "
                                    [style.max-height.px]="
                                      !getDraggableLayout(pageIdx)?.draggableCards[cardIndex]?.insertedRenderW
                                        ? getDraggableLayout(pageIdx)?.draggableCards[cardIndex]?.insertedPendingMaxH
                                        : null
                                    "
                                    loading="eager"
                                    decoding="async"
                                    alt=""
                                  />
                                  <div
                                    *ngIf="!stickerSlot.src"
                                    class="v2-ao-album-sticker-placeholder"
                                    aria-hidden="true"
                                  ></div>
                                  <div
                                    *ngIf="!stickerSlot.owned"
                                    class="v2-ao-album-locked-overlay"
                                    aria-hidden="true"
                                  >
                                    <div class="v2-ao-album-locked-icon-wrap">
                                      <svg
                                        class="v2-ao-album-locked-icon"
                                        viewBox="0 0 24 24"
                                        fill="none"
                                        stroke="currentColor"
                                        stroke-width="2"
                                        stroke-linecap="round"
                                        stroke-linejoin="round"
                                        xmlns="http://www.w3.org/2000/svg"
                                      >
                                        <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                                        <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                                      </svg>
                                    </div>
                                  </div>
                                </div>

                                <div
                                  *ngIf="!isDraggableInsertedSticker(card)"
                                  class="v2-ao-album-sticker-slot v2-ao-draggable-card-wrap"
                                  [class.v2-ao-album-sticker-slot--locked]="!stickerSlot.owned"
                                  role="button"
                                  tabindex="0"
                                  (click)="onAlbumStickerClick(card.item_id, $event)"
                                  (keydown.enter)="onAlbumStickerClick(card.item_id, $event)"
                                  [style.width.px]="
                                    card.orientation === 'P'
                                      ? getDraggableLayout(pageIdx)?.draggableCardWidthPortrait
                                      : getDraggableLayout(pageIdx)?.draggableCardWidthLandscape
                                  "
                                  [style.height.px]="
                                    card.orientation === 'P'
                                      ? getDraggableLayout(pageIdx)?.draggableCardHeightPortrait
                                      : getDraggableLayout(pageIdx)?.draggableCardHeightLandscape
                                  "
                                >
                                  <img
                                    *ngIf="stickerSlot.src"
                                    class="v2-ao-album-img"
                                    style="width: 100%; height: 100%; object-fit: cover"
                                    [src]="stickerSlot.src"
                                    loading="eager"
                                    decoding="async"
                                    alt=""
                                  />
                                  <div
                                    *ngIf="!stickerSlot.src"
                                    class="v2-ao-album-sticker-placeholder"
                                    aria-hidden="true"
                                  ></div>
                                  <div
                                    *ngIf="!stickerSlot.owned"
                                    class="v2-ao-album-locked-overlay"
                                    aria-hidden="true"
                                  >
                                    <div class="v2-ao-album-locked-icon-wrap">
                                      <svg
                                        class="v2-ao-album-locked-icon"
                                        viewBox="0 0 24 24"
                                        fill="none"
                                        stroke="currentColor"
                                        stroke-width="2"
                                        stroke-linecap="round"
                                        stroke-linejoin="round"
                                        xmlns="http://www.w3.org/2000/svg"
                                      >
                                        <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                                        <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                                      </svg>
                                    </div>
                                  </div>
                                </div>
                              </ng-container>
                            </div>
                          </div>

                          <img
                            *ngFor="let logo of (getPageDef(pageIdx).draggableLogos || []); let i = index"
                            class="v2-ao-album-img"
                            style="position: absolute"
                            [style.width.px]="getDraggableLayout(pageIdx)?.draggableLogoWidth"
                            [style.height.px]="getDraggableLayout(pageIdx)?.draggableLogoHeight"
                            [style.top]="getDraggableLayout(pageIdx)?.draggableLogos[i]?.scaledTop"
                            [style.left]="getDraggableLayout(pageIdx)?.draggableLogos[i]?.scaledLeft"
                            [src]="logo.src"
                            loading="eager"
                            decoding="async"
                            alt=""
                          />

                          <div
                            style="position: absolute"
                            *ngIf="getPageDef(pageIdx).sectionHeader?.hasDraggableTitle"
                            [style.color]="getPageDef(pageIdx).sectionHeaderColor"
                            [style.font-size]="getDraggableLayout(pageIdx)?.draggableHeader?.scaledFontSize"
                            [style.top]="getDraggableLayout(pageIdx)?.draggableHeader?.scaledTop"
                            [style.left]="getDraggableLayout(pageIdx)?.draggableHeader?.scaledLeft"
                          >{{ getPageDef(pageIdx).sectionHeader?.draggableTitleText }}</div>

                          <div
                            style="position: absolute"
                            *ngIf="getPageDef(pageIdx).sectionFooter?.hasDraggableFotter"
                            [style.color]="getPageDef(pageIdx).sectionFooterColor"
                            [style.font-size]="getDraggableLayout(pageIdx)?.draggableFooter?.scaledFontSize"
                            [style.top]="getDraggableLayout(pageIdx)?.draggableFooter?.scaledTop"
                            [style.left]="getDraggableLayout(pageIdx)?.draggableFooter?.scaledLeft"
                          >{{ getPageDef(pageIdx).sectionFooter?.draggableFotterText }}</div>

                          <div
                            *ngFor="let text of (getPageDef(pageIdx).draggableText || []); let i = index"
                            style="position: absolute"
                            [style.top]="getDraggableLayout(pageIdx)?.draggableTexts[i]?.scaledTop"
                            [style.left]="getDraggableLayout(pageIdx)?.draggableTexts[i]?.scaledLeft"
                            [style.border-radius]="getDraggableLayout(pageIdx)?.draggableTexts[i]?.scaledBorderRadius"
                            [style.background-color]="getDraggableLayout(pageIdx)?.draggableTexts[i]?.isBackgroundTransparent ? 'transparent' : getDraggableLayout(pageIdx)?.draggableTexts[i]?.backgroundColor"
                            [style.width.px]="getDraggableLayout(pageIdx)?.draggableTextWidth[i]"
                            [style.height.px]="getDraggableLayout(pageIdx)?.draggableTextHeight[i]"
                            [style.padding]="getDraggableLayout(pageIdx)?.draggableTexts[i]?.scaledPadding"
                          >
                            <quill-view
                              [content]="text.text"
                              format="json"
                              theme="bubble"
                              [ngStyle]="{
                                '--dynamic-font-size-h1': (getDraggableLayout(pageIdx)?.draggableTexts[i]?.scaledFontSizeH1 ?? 0) + 'rem',
                                '--dynamic-font-size-huge': (getDraggableLayout(pageIdx)?.draggableTexts[i]?.scaledFontSizeHuge ?? 0) + 'rem'
                              }"
                            ></quill-view>
                          </div>

                          <img
                            *ngFor="let logo of (getPageDef(pageIdx).draggableImg || []); let i = index"
                            class="v2-ao-album-img"
                            style="position: absolute"
                            [style.width.px]="getDraggableLayout(pageIdx)?.draggableImgs[i]?.width"
                            [style.height.px]="getDraggableLayout(pageIdx)?.draggableImgs[i]?.height"
                            [style.top]="getDraggableLayout(pageIdx)?.draggableImgs[i]?.scaledTop"
                            [style.left]="getDraggableLayout(pageIdx)?.draggableImgs[i]?.scaledLeft"
                            [src]="getDraggableLayout(pageIdx)?.draggableImgs[i]?.imageSrc"
                            [style.transform]="'scale(' + (logo?.scale ?? 1) + ') rotate(' + (logo?.rotation ?? 0) + 'deg)'"
                            loading="eager"
                            decoding="async"
                            alt=""
                          />
                        </section>
                      </div>
                    </ng-container>

                  </div><!-- /.album-page -->
                </div><!-- /.v2-ao-carousel-slide-inner -->
              </div><!-- /.v2-ao-carousel-slide -->
            </ng-container>

          </div><!-- /#carouselTrack -->
        </div><!-- /#carouselViewport -->

        </div><!-- /.v2-ao-carousel-shell -->
      </div><!-- /.album-card-block -->
      </div><!-- /.v2-ao-zoom-surface -->
    </div><!-- /.v2-ao-scroll-center -->
  </div><!-- /.v2-ao-body -->

  <!-- ——— Buy CTA (above) + nav: CTA starts behind nav (lower z-index), slides up to sit just above ——— -->
  <div class="v2-ao-nav-stack">
    <div
      *ngIf="showBuyAlbumButton && totalPages > 0"
      class="v2-ao-buy-peek"
      [class.v2-ao-buy-peek--visible]="buyAlbumPeekVisible"
      [attr.aria-hidden]="!buyAlbumPeekVisible"
    >
      <div
        class="v2-ao-buy-btn-shell"
        [class.v2-ao-buy-btn-shell--celebrate]="buyAlbumPeekVisible && collectionComplete"
      >
        <button
          type="button"
          class="v2-ao-buy-btn"
          (click)="onBuyAlbumClick()"
          [attr.aria-label]="'REQUEST_PHYSICAL' | translate"
          [attr.tabindex]="buyAlbumPeekVisible ? null : -1"
        >
          <ion-icon name="bag-handle-outline" class="v2-ao-buy-btn__icon" aria-hidden="true"></ion-icon>
          <span class="v2-ao-buy-btn__label">{{ 'REQUEST_PHYSICAL' | translate }}</span>
        </button>
      </div>
    </div>

    <div class="v2-ao-nav">
      <button
        type="button"
        class="v2-ao-nav-arrow v2-ao-nav-arrow--prev"
        (click)="prevPage()"
        [class.v2-ao-nav-arrow--hidden]="totalPages < 2"
        [disabled]="activePageIndex <= 0 || totalPages < 2"
        [attr.aria-disabled]="activePageIndex <= 0 ? true : null"
        aria-label="Previous page"
      >
        <ion-icon name="chevron-back-outline"></ion-icon>
      </button>

      <div class="v2-ao-nav-center" *ngIf="totalPages > 0">
        <span class="v2-ao-nav-count" aria-hidden="true">
          {{ activePageIndex + 1 }} / {{ totalPages }}
        </span>
        <div class="v2-ao-nav-track" role="tablist" aria-label="Pages">
          <button
            *ngFor="let i of pageIndexList; trackBy: trackNavByIndex"
            #navSeg
            type="button"
            role="tab"
            class="v2-ao-nav-seg"
            [class.v2-ao-nav-seg--active]="i === activePageIndex"
            (click)="goToPage(i)"
            [attr.aria-selected]="i === activePageIndex"
            [attr.aria-label]="'Page ' + (i + 1)"
          ></button>
        </div>
      </div>

      <button
        type="button"
        class="v2-ao-nav-arrow v2-ao-nav-arrow--next"
        (click)="nextPage()"
        [class.v2-ao-nav-arrow--hidden]="totalPages < 2"
        [disabled]="activePageIndex >= totalPages - 1 || totalPages < 2"
        [attr.aria-disabled]="activePageIndex >= totalPages - 1 ? true : null"
        aria-label="Next page"
      >
        <ion-icon name="chevron-forward-outline"></ion-icon>
      </button>
    </div>
  </div>

</div>
